import React, { useState } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { SelectFieldSimple, SelectFieldSimpleProps } from '@lumapps/combobox/components/SelectFieldSimple';
import { useDataAttributes } from '@lumapps/data-attributes';
import {
    Alignment,
    ColorPalette,
    ColorVariant,
    FlexBox,
    ListItem,
    Orientation,
    SelectVariant,
    Size,
    Text,
    Typography,
} from '@lumapps/lumx/react';

import './NavigationListFilter.scss';
import { SCOPE } from '../../constants';

const CLASSNAME = 'navigation-list-filter';

export interface NavigationListFilterSelectChoice {
    id: string;
    name: string;
}

export interface NavigationListFilterSelect {
    /** The choices to display in the select */
    choices: Array<NavigationListFilterSelectChoice>;
    /** The selected value */
    selected?: NavigationListFilterSelectChoice;
    /** Callback when a value is selected */
    onSelect: (selectedChoice?: NavigationListFilterSelectChoice) => void;
    /** Label displayed next to the select */
    label?: string;
    /** Label displayed within the select when "displayedValue" is undefined selected */
    placeholder: string;
    /** The element that qualifies the filter for data-attribute tags */
    dataAttributeElement: string;
}

/** "Select" filter element for the navigation list */
export const NavigationListFilterSelect: React.FC<NavigationListFilterSelect> = ({
    choices,
    selected,
    label,
    placeholder,
    onSelect,
    dataAttributeElement,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(SCOPE);

    const [isOpen, setIsOpen] = useState(false);

    const clearSelected = () => {
        if (onSelect) {
            onSelect(undefined);
        }
    };

    const choiceRenderer: SelectFieldSimpleProps<NavigationListFilterSelectChoice>['choiceRenderer'] =
        React.useCallback((...props) => {
            const [choice, isSelected, onSelect] = props;
            return (
                <ListItem isSelected={isSelected} key={choice.id} onItemSelected={onSelect(choice)} size={Size.tiny}>
                    {choice.name}
                </ListItem>
            );
        }, []);

    return (
        <FlexBox className={block()} orientation={Orientation.horizontal} hAlign={Alignment.center} gap={Size.regular}>
            {/**
             * /!\ This is not a good practice. /!\
             * In order to be accessible, this should be a `label` linked to the select using `htmlFor`.
             * However since the Select already doesn't follow an accessible pattern, linking a label to it might
             * actually be more confusing.
             * */}
            {label && (
                <Text as="p" typography={Typography.body1} color={ColorPalette.dark} colorVariant={ColorVariant.L2}>
                    {label}
                </Text>
            )}
            <SelectFieldSimple<NavigationListFilterSelectChoice>
                className={element('select')}
                isOpen={isOpen}
                value={selected || null}
                label={placeholder}
                setOpen={setIsOpen}
                choices={choices}
                onChange={onSelect}
                selectVariant={SelectVariant.chip}
                onSelectClear={clearSelected}
                choiceRenderer={choiceRenderer}
                getValueName={({ name }) => name}
                getValueId={({ id }) => id}
                selectProps={{
                    ...get({ element: dataAttributeElement, action: selected?.name || 'click' }),
                }}
            />
        </FlexBox>
    );
};
